<template>
	<span
		v-if="isVisible"
		:class="
			bemm('', [
				color,
				disabled ? 'disabled' : '',
				hasActions ? 'has-actions' : '',
			])
		"
		:style="color ? `--badge-color: var(--color-${color});` : ''"
	>
		<Icon v-if="icon" :name="icon" :class="bemm('icon')" />
		<slot>{{ displayValue }}</slot>
		<Actions
			v-if="hasActions && !disabled"
			:class="bemm('actions')"
			:actions="props.actions"
		/>
	</span>
</template>

<script lang="ts" setup>
import { computed, PropType, useSlots } from 'vue';
import { useBemm } from 'bemm';
import { Color } from '@arritech/shared/types';
import { IconNameOrString } from '../Icon';
import { Icon } from '../Icon';
import { Actions } from '../Actions';
import type { ActionItem } from '../Actions';

type BadgeValue = string | number;

const props = defineProps({
	value: {
		type: [String, Number] as PropType<BadgeValue>,
		default: null,
	},
	color: {
		type: String as PropType<Color | 'default'>,
		default: 'primary',
	},
	icon: {
		type: String as PropType<IconNameOrString>,
		default: null,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	actions: {
		type: Array as PropType<ActionItem[]>,
		default: () => [],
	},
});

const bemm = useBemm('ar-badge', {
	return: 'string',
	includeBaseClass: true,
});

const slots = useSlots();

const isVisible = computed(() => {
	if (slots.default) {
		return true;
	}
	if (props.value === null || props.value === undefined || props.value === '') {
		return false;
	}
	return true;
});

const displayValue = computed(() => {
	return props.value;
});

const hasActions = computed(() => {
	return props.actions.length > 0;
});
</script>

<style lang="scss">
.ar-badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 1.5rem;
	height: 1.5rem;
	padding: var(--space-xs) var(--space-s);
	border-radius: 999px;
	font-size: var(--font-size-xs);
	line-height: 1;
	font-weight: var(--font-weight-bold);
	background: color-mix(in srgb, var(--badge-color), transparent 85%);
	color: color-mix(in srgb, var(--badge-color), var(--color-foreground) 25%);
	border: 1px solid var(--badge-color);
	flex-shrink: 0;
	gap: var(--space);
	position: relative;

	--badge-color: var(--color-primary);

	&--default {
		--badge-color: var(--color-secondary);
	}

	&--disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	&__icon {
		color: var(--badge-color);
	}

	&__actions {
		position: absolute;
		right: var(--space-xs);
		top: 50%;
		transform: translateY(-50%);
		opacity: 0;
		pointer-events: none;
		transition: opacity 120ms ease;
	}

	&--has-actions:hover &__actions,
	&--has-actions:focus-within &__actions {
		opacity: 1;
		pointer-events: auto;
	}
}
</style>
